import { MaterialIcons } from '@expo/vector-icons';
import { DrawerActions } from '@react-navigation/native';
import { useNavigation, usePathname, useRouter } from 'expo-router';
import React, { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { ActivityIndicator, FlatList, Pressable, TouchableOpacity, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { ThemedText } from '@/components/ThemedText';
import { ThemedView } from '@/components/ThemedView';
import { IconSymbol } from '@/components/ui/IconSymbol';
import AppStyles from '@/constants/Styles';
import { useBackgroundCampaigns } from '@/hooks/useBackgroundFetch';
import { useThemeColor } from '@/hooks/useThemeColor';
import { Campaign, Election, useNavigationStore } from '@/stores/navigationStore';


interface GroupedCampaign {
  type: 'header' | 'campaign';
  election?: Election;
  campaign?: Campaign;
  electionId?: string;
}

export default function CampaignListPage() {
  const { getSelectedCampaign, setSelectedCampaignId, getSelectedBranch, getElections, getElectionForCampaign } = useNavigationStore();
  const selectedCampaign = getSelectedCampaign();
  const selectedBranch = getSelectedBranch();
  const [localSelectedCampaign, setLocalSelectedCampaign] = useState<Campaign | null>(selectedCampaign);
  const [expandedElections, setExpandedElections] = useState<Set<string>>(new Set());
  const [showPastCampaigns, setShowPastCampaigns] = useState(false);
  const hasInitializedExpanded = useRef(false);
  const hasInitializedPastExpanded = useRef(false);
  const router = useRouter();
  const navigation = useNavigation();
  const pathname = usePathname();
  const insets = useSafeAreaInsets();

  // Theme-aware colors
  const tintColor = useThemeColor({}, 'tint');
  const errorColor = useThemeColor({}, 'errorText' as any);
  const cardBackgroundColor = useThemeColor({}, 'cardBackground' as any);
  const backgroundColor = useThemeColor({}, 'background') + "CC";
  const iconColor = useThemeColor({}, 'icon');

  // Use background fetch hook for campaigns
  const { data: campaigns, loading, error } = useBackgroundCampaigns(
    selectedBranch?.branch_short_name || ''
  );

  // Get elections for this branch
  const elections = selectedBranch ? getElections(selectedBranch.branch_short_name) : null;

  useLayoutEffect(() => {
    navigation.setOptions({
      headerLeft: () => (
        <TouchableOpacity onPress={() => navigation.dispatch(DrawerActions.toggleDrawer())} style={{ paddingLeft: 15 }}>
          <IconSymbol name="chevron.right" size={28} color={tintColor} />
        </TouchableOpacity>
      ),
      headerBackVisible: false,
    });
  }, [navigation, router, selectedBranch, pathname, tintColor]);

  // Separate active and past campaigns (must be called before conditional returns)
  const activeCampaigns = useMemo(() => {
    if (!campaigns) return [];
    return campaigns.filter(campaign => campaign.is_active);
  }, [campaigns]);

  const pastCampaigns = useMemo(() => {
    if (!campaigns) return [];
    return campaigns.filter(campaign => !campaign.is_active);
  }, [campaigns]);

  // Group campaigns by election
  const groupCampaignsByElection = useCallback((campaignList: Campaign[]): GroupedCampaign[] => {
    if (!selectedBranch || !elections) return [];

    // Group campaigns by election
    const electionGroups = new Map<string, { election: Election; campaigns: Campaign[] }>();
    const ungroupedCampaigns: Campaign[] = [];
    // Track which campaigns we've already added to prevent duplicates
    const addedCampaignIds = new Set<string>();

    campaignList.forEach(campaign => {
      // Skip if we've already added this campaign
      if (addedCampaignIds.has(campaign.boundary_id)) {
        return;
      }

      const election = getElectionForCampaign(campaign, selectedBranch.branch_short_name);
      if (election) {
        if (!electionGroups.has(election.election_id)) {
          electionGroups.set(election.election_id, { election, campaigns: [] });
        }
        electionGroups.get(election.election_id)!.campaigns.push(campaign);
        addedCampaignIds.add(campaign.boundary_id);
      } else {
        ungroupedCampaigns.push(campaign);
        addedCampaignIds.add(campaign.boundary_id);
      }
    });

    // Build flat list with headers and campaigns
    const result: GroupedCampaign[] = [];
    
    // Sort election groups by polling day (most recent first)
    const sortedGroups = Array.from(electionGroups.values()).sort((a, b) => {
      const dateA = new Date(a.election.polling_day).getTime();
      const dateB = new Date(b.election.polling_day).getTime();
      return dateB - dateA; // Descending order (newest first)
    });

    sortedGroups.forEach(({ election, campaigns }) => {
      // Add header
      result.push({
        type: 'header',
        election,
        electionId: election.election_id,
      });
      
      // Sort campaigns by boundary_name
      campaigns.sort((a, b) => (a.boundary_name || a.code || '').localeCompare(b.boundary_name || b.code || ''));
      
      // Add campaigns
      campaigns.forEach(campaign => {
        result.push({
          type: 'campaign',
          campaign,
          electionId: election.election_id,
        });
      });
    });

    // Add ungrouped campaigns at the end
    if (ungroupedCampaigns.length > 0) {
      ungroupedCampaigns.sort((a, b) => (a.boundary_name || a.code || '').localeCompare(b.boundary_name || b.code || ''));
      ungroupedCampaigns.forEach(campaign => {
        result.push({
          type: 'campaign',
          campaign,
        });
      });
    }

    return result;
  }, [selectedBranch, elections, getElectionForCampaign]);

  const activeGrouped = useMemo(() => groupCampaignsByElection(activeCampaigns), [activeCampaigns, groupCampaignsByElection]);
  const pastGrouped = useMemo(() => groupCampaignsByElection(pastCampaigns), [pastCampaigns, groupCampaignsByElection]);

  // Map election IDs to campaign counts
  const electionCampaignCounts = useMemo(() => {
    const counts = new Map<string, number>();
    [...activeGrouped, ...pastGrouped].forEach(item => {
      if (item.type === 'campaign' && item.electionId) {
        counts.set(item.electionId, (counts.get(item.electionId) || 0) + 1);
      }
    });
    return counts;
  }, [activeGrouped, pastGrouped]);

  // Initialize expanded elections: first active election expanded by default (only once)
  React.useEffect(() => {
    if (!hasInitializedExpanded.current && activeGrouped.length > 0) {
      // Find the first election header
      const firstElectionHeader = activeGrouped.find(item => item.type === 'header' && item.electionId);
      if (firstElectionHeader?.electionId) {
        setExpandedElections(new Set([firstElectionHeader.electionId]));
        hasInitializedExpanded.current = true;
      }
    }
  }, [activeGrouped]);

  // Toggle election expansion
  const toggleElection = useCallback((electionId: string) => {
    setExpandedElections(prev => {
      const newSet = new Set(prev);
      if (newSet.has(electionId)) {
        newSet.delete(electionId);
      } else {
        newSet.add(electionId);
      }
      return newSet;
    });
  }, []);

  // Build final list data (must be called before conditional returns)
  const listData = useMemo(() => {
    const data: (GroupedCampaign | { type: 'past-link' })[] = [];
    const addedCampaignIds = new Set<string>(); // Track campaigns to prevent duplicates
    const addedElectionIds = new Set<string>(); // Track election headers to prevent duplicates
    
    // Add active campaigns with expansion logic
    let currentElectionId: string | undefined;
    activeGrouped.forEach(item => {
      if (item.type === 'header' && item.electionId) {
        currentElectionId = item.electionId;
        // Only add header if not already added
        if (!addedElectionIds.has(item.electionId)) {
          data.push(item);
          addedElectionIds.add(item.electionId);
        }
      } else if (item.type === 'campaign' && item.campaign) {
        // Only add campaign if its election is expanded and not already added
        if (currentElectionId && expandedElections.has(currentElectionId)) {
          if (!addedCampaignIds.has(item.campaign.boundary_id)) {
            data.push(item);
            addedCampaignIds.add(item.campaign.boundary_id);
          }
        }
      } else {
        // For ungrouped campaigns or other types, always add (if not duplicate)
        if (item.type === 'campaign' && item.campaign) {
          if (!addedCampaignIds.has(item.campaign.boundary_id)) {
            data.push(item);
            addedCampaignIds.add(item.campaign.boundary_id);
          }
        } else {
          data.push(item);
        }
      }
    });
    
    // Add past campaigns link if there are any and not yet shown
    if (pastGrouped.length > 0 && !showPastCampaigns) {
      data.push({ type: 'past-link' });
    }
    
    // Add past campaigns if they should be shown
    if (showPastCampaigns && pastGrouped.length > 0) {
      let pastCurrentElectionId: string | undefined;
      pastGrouped.forEach(item => {
        if (item.type === 'header' && item.electionId) {
          pastCurrentElectionId = item.electionId;
          // Only add header if not already added
          if (!addedElectionIds.has(item.electionId)) {
            data.push(item);
            addedElectionIds.add(item.electionId);
          }
        } else if (item.type === 'campaign' && item.campaign) {
          // Only add campaign if its election is expanded and not already added
          if (pastCurrentElectionId && expandedElections.has(pastCurrentElectionId)) {
            if (!addedCampaignIds.has(item.campaign.boundary_id)) {
              data.push(item);
              addedCampaignIds.add(item.campaign.boundary_id);
            }
          }
        } else {
          // For other types, add if not duplicate
          if (item.type === 'campaign' && item.campaign) {
            if (!addedCampaignIds.has(item.campaign.boundary_id)) {
              data.push(item);
              addedCampaignIds.add(item.campaign.boundary_id);
            }
          } else {
            data.push(item);
          }
        }
      });
    }
    
    return data;
  }, [activeGrouped, pastGrouped, expandedElections, showPastCampaigns]);

  // Show loading state
  if (loading) {
    return (
      <ThemedView style={{ flex: 1 }}>
        <View style={[AppStyles.spinnerOverlay, { backgroundColor: `${backgroundColor}` }]}>
          <ActivityIndicator size="large" color={tintColor} />
        </View>
      </ThemedView>
    );
  }

  // Show error state
  if (error) {
    return (
      <ThemedView style={{ flex: 1 }}>
        <View style={[AppStyles.spinnerOverlay, { backgroundColor: `${backgroundColor}` }]}>
          <ThemedText style={{ color: errorColor, textAlign: 'center' }}>
            Error loading campaigns: {error.message}
          </ThemedText>
        </View>
      </ThemedView>
    );
  }

  // Show empty state
  if (!campaigns || campaigns.length === 0) {
    return (
      <ThemedView style={{ flex: 1 }}>
        <View style={[AppStyles.spinnerOverlay, { backgroundColor: `${backgroundColor}` }]}>
          <ThemedText style={{ textAlign: 'center' }}>
            No campaigns found for this branch.
          </ThemedText>
        </View>
      </ThemedView>
    );
  }

  const renderItem = ({ item }: { item: GroupedCampaign | { type: 'past-link' } }) => {
    // Past campaigns link - hyperlink style
    if (item.type === 'past-link') {
      return (
        <Pressable
          onPress={() => setShowPastCampaigns(true)}
          style={{
            paddingHorizontal: 16,
            paddingVertical: 12,
            marginTop: 8,
          }}
        >
          <ThemedText style={[AppStyles.headerText, { color: tintColor, textDecorationLine: 'underline' }]}>
            View Past Campaigns
          </ThemedText>
        </Pressable>
      );
    }

    // Election header
    if (item.type === 'header' && item.election && item.electionId) {
      const electionDate = new Date(item.election.polling_day);
      const formattedDate = electionDate.toLocaleDateString('en-GB', {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
      });
      const isExpanded = expandedElections.has(item.electionId);
      const campaignCount = electionCampaignCounts.get(item.electionId) || 0;

      return (
        <TouchableOpacity
          onPress={() => toggleElection(item.electionId!)}
          style={{
            paddingHorizontal: 16,
            paddingVertical: 12,
            backgroundColor: cardBackgroundColor,
            marginTop: 8,
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <View style={{ flex: 1 }}>
            <View style={{ flexDirection: 'row', alignItems: 'center' }}>
              <MaterialIcons
                name={isExpanded ? 'expand-less' : 'expand-more'}
                size={24}
                color={iconColor}
                style={{ marginRight: 8 }}
              />
              <ThemedText style={[AppStyles.headerText, { fontWeight: '600', flex: 1 }]}>
                {item.election.election_name}
              </ThemedText>
            </View>
            <ThemedText style={{ fontSize: 14, opacity: 0.7, marginTop: 4, marginLeft: 32 }}>
              {formattedDate} ({campaignCount} {campaignCount === 1 ? 'campaign' : 'campaigns'})
            </ThemedText>
          </View>
        </TouchableOpacity>
      );
    }

    // Campaign item
    if (item.type === 'campaign' && item.campaign) {
      return (
        <Pressable
          style={[
            AppStyles.listItemLeftAligned,
            { backgroundColor: cardBackgroundColor },
            localSelectedCampaign?.boundary_name === item.campaign.boundary_name && AppStyles.selectedItem,
          ]}
          onPress={async () => {
            setLocalSelectedCampaign(item.campaign!);
            setSelectedCampaignId(selectedBranch?.branch_short_name || '', item.campaign!.boundary_id);
            router.replace(`/${selectedBranch?.branch_short_name}/campaign/${item.campaign!.boundary_id}`);
          }}
        >
          <ThemedText style={AppStyles.headerText}>
            {item.campaign.boundary_name || item.campaign.code}
          </ThemedText>
        </Pressable>
      );
    }

    return null;
  };

  const keyExtractor = (item: GroupedCampaign | { type: 'past-link' }, index: number) => {
    if (item.type === 'past-link') {
      return 'past-link';
    }
    if (item.type === 'header') {
      return `header-${item.electionId || index}`;
    }
    if (item.type === 'campaign' && item.campaign) {
      return item.campaign.boundary_id || item.campaign.code || `campaign-${index}`;
    }
    return `item-${index}`;
  };

  return (
    <ThemedView style={{ flex: 1 }}>
      <FlatList
        data={listData}
        keyExtractor={keyExtractor}
        renderItem={renderItem}
        contentContainerStyle={{ paddingBottom: insets.bottom }}
      />
    </ThemedView>
  );
} 